import SalviaLayout from "@/components/SalviaLayout";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { usePageTitle } from "@/hooks/usePageTitle";
import { KPICard, KPICardRow, AdesaoBar } from "@/components/dashboard";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, Activity, CheckCircle2, Send } from "lucide-react";

export default function CanalColeta() {
  usePageTitle("Coleta do Canal");
  const { user } = useAuth();
  const isChannelRole = user?.role === "channel_admin" || user?.role === "channel_admin_multi" || user?.role === "channel_operator";
  const { data, isLoading, error } = trpc.dashboards.channel.collection.useQuery(undefined, {
    enabled: !!user && isChannelRole,
    refetchOnWindowFocus: false,
  });

  if (error) {
    return (
      <SalviaLayout title="Dashboard — Coleta do Canal">
        <Card className="p-8 text-center">
          <AlertTriangle className="mx-auto mb-4 text-destructive" size={48} />
          <p className="text-muted-foreground">{error.message}</p>
        </Card>
      </SalviaLayout>
    );
  }

  const loading = isLoading || !data;
  const statusMap: Record<string, { count: number; companies: number }> = {};
  if (data?.campaignsByStatus) {
    for (const s of data.campaignsByStatus) statusMap[s.status] = { count: s.count, companies: s.companies };
  }

  return (
    <SalviaLayout title="Dashboard — Coleta do Canal">
      <div className="space-y-6">
        <KPICardRow>
          <KPICard
            label="Campanhas Ativas"
            value={statusMap.ACTIVE?.count ?? 0}
            icon={Activity}
            iconColor="text-blue-600"
            iconBg="bg-blue-100"
            subtitle={`${statusMap.ACTIVE?.companies ?? 0} empresas`}
            loading={loading}
          />
          <KPICard
            label="Encerradas"
            value={statusMap.CLOSED?.count ?? 0}
            icon={CheckCircle2}
            iconColor="text-emerald-600"
            iconBg="bg-emerald-100"
            loading={loading}
          />
          <KPICard
            label="Rascunhos"
            value={statusMap.DRAFT?.count ?? 0}
            icon={Send}
            iconColor="text-slate-500"
            iconBg="bg-slate-100"
            loading={loading}
          />
          <KPICard
            label="Total Respostas"
            value={data?.activeCampaigns.reduce((s: number, c: any) => s + c.completedSubmissions, 0) ?? 0}
            icon={CheckCircle2}
            iconColor="text-green-600"
            iconBg="bg-green-100"
            subtitle="nas campanhas ativas"
            loading={loading}
          />
        </KPICardRow>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold">Campanhas Ativas — Taxa de Resposta</h3>
            <span className="text-xs text-muted-foreground">{data?.activeCampaigns.length ?? 0} campanhas</span>
          </div>
          {loading ? (
            <div className="space-y-3">
              {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : (data?.activeCampaigns.length ?? 0) === 0 ? (
            <p className="text-center text-muted-foreground py-8">Nenhuma campanha ativa</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Empresa</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-muted-foreground uppercase">Escopo</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-muted-foreground uppercase">Vidas</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-muted-foreground uppercase">Respostas</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase w-36">Adesão</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Encerra</th>
                  </tr>
                </thead>
                <tbody>
                  {data!.activeCampaigns.map((c: any) => (
                    <tr key={c.campaignId} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="px-3 py-2.5 font-medium">{c.companyName}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{c.scopeCount}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{c.totalVidas}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{c.completedSubmissions}</td>
                      <td className="px-3 py-2.5">
                        <AdesaoBar completed={c.completedSubmissions} total={c.totalVidas} />
                      </td>
                      <td className="px-3 py-2.5 text-xs text-muted-foreground">
                        {c.endsAt ? new Date(c.endsAt).toLocaleDateString("pt-BR") : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </SalviaLayout>
  );
}
